import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subject,
  tap,
} from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { NzResultStatusType } from 'ng-zorro-antd/result';
import { NzTabChangeEvent } from 'ng-zorro-antd/tabs';

import {
  AppointmentServiceType,
  BusinessLogicStatus,
  CatalogClient,
  HelixExpressHxidAvailabilityResponse,
  NomenclatureResponse,
} from '@medindex-webapi';
import { HttpResponseStatuses } from '@shared/enums/http-statuses';
import { ErrorMessages } from '@shared/types/error-messages.enum';
import { ServiceError } from '@shared/types/service-error.type';
import { StorageService } from '@shared/core/services/storage.service';
import { FeatureFlags } from '@shared/enums/featureFlags';
import { CityId } from '@shared/types/storage';
import { HelixRoutes } from '@shared/enums/routes';
import { cityIn } from '@shared/utils/declination-cities';
import { firstCharToLowerCase } from '@shared/utils/utils';
import { JsonLdStorage } from '@shared/services/meta/jsonld-schema-storage';
import { MetaService } from '@shared/services/meta/meta.service';
import { BreadcrumbItem } from '@shared/modules/breadcrumb/types/breadcrumbItem';
import { IServiceInfo } from '@shared/modules/modal-appointment-doctor/interfaces/modal-appointment-doctor.interfaces';
import { ModalAppointmentDoctorComponent } from '@shared/modules/modal-appointment-doctor/modal-appointment-doctor.component';
import { ModalService } from '@shared/services/modal/modal.service';
import { ModalSize } from '@shared/enums/modal-sizes';
import { ModalTitle } from '@shared/enums/modal-title.enum';
import { TypeNomenclature } from '@shared/enums/type-nomenclature';
import { DatasourceService } from '@shared/core/services/datasource.service';
import { RegionsService } from '@shared/services/regions.service';
import { ErrorService } from '@shared/core/services/error.service';
import { YmGoalAppointment } from '@shared/services/yandex-metrica/enums/appointment.goals';
import { YmService } from '@shared/services/yandex-metrica/ym.service';
import { CatalogItemTab } from '../enums/catalog-item-tab.enum';

@Injectable()
export class ItemRepositoryService implements OnDestroy {
  constructor(
    private storage: StorageService,
    private router: Router,
    private errorService: ErrorService,
    private modalService: ModalService,
    private metaService: MetaService,
    private catalogClient: CatalogClient,
    private datasource: DatasourceService,
    private ymService: YmService,
    private regions: RegionsService
  ) {
    this.loadData();
    this.errorHandler();
  }

  private _hxid = new BehaviorSubject<string>('');
  private _isEmptyNomenclature = new BehaviorSubject<boolean>(true);
  private _nomenclature = new BehaviorSubject<NomenclatureResponse | null>(
    null
  );
  private _nomenclatureErrorEvent = new BehaviorSubject<ServiceError | null>(
    null
  );
  private _isShowHelixExpressAd = new BehaviorSubject(false);
  private _selectedTab$ = new BehaviorSubject<CatalogItemTab>(
    CatalogItemTab.Description
  );

  hxid$ = this._hxid.asObservable();
  isEmptyNomenclature$ = this._isEmptyNomenclature.asObservable();
  nomenclature$ = this._nomenclature.asObservable();
  nomenclatureErrorEvent$ = this._nomenclatureErrorEvent.asObservable();
  isShowHelixExpressAd$ = this._isShowHelixExpressAd.asObservable();
  ngUnsubscribe$ = new Subject<void>();
  tabSet$ = this.nomenclature$.pipe(
    map((nomenclature) => {
      const tabs: CatalogItemTab[] = [CatalogItemTab.Description];

      if (nomenclature?.parts?.length) tabs.push(CatalogItemTab.Complex);
      if (nomenclature?.patientPreparations?.length)
        tabs.push(CatalogItemTab.Preparations);
      if (nomenclature?.drugInfluenceGroups?.length)
        tabs.push(CatalogItemTab.Indications);

      return tabs;
    })
  );
  currentTabIndex$ = combineLatest([this._selectedTab$, this.tabSet$]).pipe(
    map(([activeTab, tabSet]) => tabSet.indexOf(activeTab))
  );

  appointmentType$ = this.nomenclature$.pipe(
    map(
      (nomenclature) =>
        nomenclature?.attributes?.appointmentType ??
        AppointmentServiceType.Default
    )
  );

  title$ = this.nomenclature$.pipe(
    filter((nomenclature) => !!nomenclature),
    switchMap((nomenclature) =>
      this.regions.getTitleWithCity(
        nomenclature?.nomenclatureInfo?.itemTitle ?? ''
      )
    )
  );

  description$ = this.nomenclature$.pipe(
    filter(
      (nomenclature): nomenclature is NomenclatureResponse => !!nomenclature
    ),
    map((nomenclature) => {
      return `${
        nomenclature.nomenclatureInfo?.itemTitle ?? ''
      } – сдать анализы в современной медицинской лаборатории Хеликс в ${cityIn(
        nomenclature.city ?? ''
      )}, цена ${nomenclature.price} ${
        nomenclature.currency?.name
      }, срок выполнения ${firstCharToLowerCase(
        nomenclature.deadline ?? ''
      )}. ✅ Оплачивайте баллами до 100% стоимости анализов!`;
    })
  );

  breadcrumbs$: Observable<BreadcrumbItem[]> = this.title$.pipe(
    map((title) => {
      return [
        { href: '/', text: 'Главная' },
        { href: `/${HelixRoutes.Catalog}`, text: 'Сдать анализы' },
        { href: '', text: title },
      ];
    })
  );

  simpleName$ = this.nomenclature$.pipe(
    map((nomenclature) => {
      if (!nomenclature) return '';
      if (nomenclature.parts?.length) return TypeNomenclature.Complex;
      if (nomenclature.isBiomaterialService) return TypeNomenclature.Service;
      return TypeNomenclature.Analysis;
    })
  );

  nomenclatureError$ = this.nomenclatureErrorEvent$.pipe(
    filter((event): event is ServiceError => !!event),
    map((event) => {
      return {
        status: String(event.status) as NzResultStatusType,
        title: event.title,
      };
    })
  );

  price$: Observable<number> = combineLatest([
    this.appointmentType$,
    this.nomenclature$,
  ]).pipe(
    map(([appointmentType, nomenclature]) => {
      if (!nomenclature) return 0;

      return appointmentType === AppointmentServiceType.Vaccination
        ? nomenclature.price +
            (nomenclature.companionBiomaterialServices?.reduce(
              (a, b) => a + b.price,
              0
            ) ?? 0)
        : nomenclature.price;
    })
  );

  priceWithoutDiscount$: Observable<number | null> = this.nomenclature$.pipe(
    map((nomenclature) => (!nomenclature ? null : nomenclature.marketPrice))
  );

  setHxid(hxid: string): void {
    this._hxid.next(hxid);
  }

  setTab(event: NzTabChangeEvent): void {
    const tab = event.tab.nzTitle as CatalogItemTab;
    this._selectedTab$.next(tab);
  }

  loadData(): void {
    combineLatest([this.hxid$, this.storage.cityId$])
      .pipe(
        filter(([hxid, cityId]) => !!hxid && !!cityId),
        this.regions.getWaitForAliasAppliedToUrlPipeOperator(),
        switchMap(([hxid, cityId]) =>
          combineLatest([
            this._getNomenclature(hxid, cityId as number),
            this._checkAvailableHelixExpressAd(hxid, cityId),
            of(hxid),
          ]).pipe(first())
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([nomenclature, ad, hxid]) => {
        this._nomenclature.next(nomenclature);
        this._isShowHelixExpressAd.next(ad);
        this._setIsEmptyNomenclature(nomenclature);
        this.metaService.title(
          (nomenclature.nomenclatureInfo?.itemTitle || `Анализ ${hxid}`) +
            ` – сдать анализы в ${cityIn(
              nomenclature.city ?? ''
            )}, цены в медицинской лаборатории Хеликс`
        );
        const synonyms = [
          nomenclature.nomenclatureInfo?.russianSynonyms,
          nomenclature.nomenclatureInfo?.englishSynonyms
        ].filter((s) => !!s).join(', ');
        this.metaService.keywords(synonyms);
        const schema = JsonLdStorage.Product(nomenclature);
        this.metaService.insertJsonLdSchema(schema);
      });
  }

  clear(): void {
    this._nomenclature.next(null);
    this._nomenclatureErrorEvent.next(null);
    this._selectedTab$.next(CatalogItemTab.Description);
  }

  private errorHandler(): void {
    this.errorService.error$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((error) => !!error)
      )
      .subscribe((error) => {
        if (error === BusinessLogicStatus.CovidRedirectKz)
          this.router.navigate([HelixRoutes.CatalogItem, '43-2616']);

        if (error === BusinessLogicStatus.CovidRedirectRu)
          this.router.navigate([HelixRoutes.CatalogItem, '09-185']);
      });
  }

  private _setIsEmptyNomenclature(nomenclature: NomenclatureResponse): void {
    const isEmptyNomenclature =
      !nomenclature.kbNomenclatureInfo?.containerUrl &&
      !nomenclature.kbNomenclatureInfo?.shortDescription &&
      !nomenclature.kbNomenclatureInfo?.inKnowlengeBase &&
      !nomenclature.hasExampleFile &&
      !nomenclature.deadline &&
      !this.isArrayExistNotEmpty(nomenclature.explorationMethods) &&
      !this.isArrayExistNotEmpty(nomenclature.companionBiomaterialServices) &&
      !this.isArrayExistNotEmpty(nomenclature.parts) &&
      !this.isArrayExistNotEmpty(nomenclature.patientPreparations) &&
      !this.isArrayExistNotEmpty(nomenclature.drugInfluenceGroups);

    this._isEmptyNomenclature.next(isEmptyNomenclature);
  }

  private isArrayExistNotEmpty = (arr: Array<unknown> | null): boolean => {
    if (!arr) return false;
    return arr.length > 0;
  };

  private _showFetchNomenclatureError(status: number, title: string): void {
    let customTitle: string = title;
    let customStatus = status;

    switch (status) {
      case HttpResponseStatuses.BadRequest:
      case HttpResponseStatuses.NotFound: {
        customTitle = ErrorMessages.NotFound;
        customStatus = HttpResponseStatuses.NotFound;
        break;
      }
      default:
      case HttpResponseStatuses.InternalServerError: {
        customTitle = ErrorMessages.Common;
      }
    }

    this._nomenclatureErrorEvent.next({
      status: customStatus,
      title: customTitle,
    });
  }

  private _getNomenclature(
    hxid: string,
    cityId: number
  ): Observable<NomenclatureResponse> {
    return this.catalogClient.getNomenclatureWithCityId(hxid, cityId).pipe(
      map((nomenclature) => {
        if (
          nomenclature.attributes?.appointmentType ===
          AppointmentServiceType.DoublePcr
        ) {
          nomenclature.biomaterialRules =
            nomenclature.biomaterialRules?.concat(
              nomenclature.biomaterialRules
            ) ?? [];
          nomenclature.biomaterialRules = nomenclature.biomaterialRules?.map(
            (rule) => {
              // eslint-disable-next-line no-param-reassign
              rule.isRequired = true;
              return rule;
            }
          );
        }

        const extendedNomenclature = nomenclature as NomenclatureResponse;
        return extendedNomenclature;
      }),
      catchError((error) => {
        this._showFetchNomenclatureError(error.status, error.statusText);
        return this.datasource.errorHandler(error);
      })
    );
  }

  private _checkAvailableHelixExpressAd(
    hxid: string,
    cityId: CityId
  ): Observable<boolean> {
    return this.storage.configuration$.pipe(
      filter(
        (config) =>
          config.featureFlags?.includes(FeatureFlags.HelixExpressAd) ?? false
      ),
      switchMap(() =>
        this.catalogClient.getHelixExpressAvailability(cityId, [hxid])
      ),
      map(
        (data: HelixExpressHxidAvailabilityResponse[]) =>
          !!data.filter((item) => item.isAvailable).length
      ),
      catchError((error) =>
        this.datasource.errorHandler(error).pipe(catchError(() => of(false)))
      )
    );
  }

  openModalAppointmentDoctor(): void {
    combineLatest([this.price$, this.nomenclature$])
      .pipe(
        first(),
        tap(
          ([price, nomenclature]: [
            number,
            NomenclatureResponse | null
          ]): void => {
            if (!nomenclature) return;

            const serviceInfo: IServiceInfo = {
              price: price.toString(),
              currency: nomenclature.currency?.symbol ?? '',
              cityName: nomenclature.city,
              hxid: nomenclature.hxid ?? '',
              description: nomenclature.nomenclatureInfo?.itemTitle ?? '',
            };

            this.createModalAppointmentDoctor(serviceInfo);
          }
        )
      )
      .subscribe();
  }

  private createModalAppointmentDoctor(serviceInfo: IServiceInfo): void {
    this.modalService.create({
      nzTitle: ModalTitle.MakeAppointment,
      nzContent: ModalAppointmentDoctorComponent,
      nzStyle: {
        width: ModalSize.Form,
      },
      nzClassName: 'modal-with-bg',
      nzComponentParams: {
        serviceInfo,
      },
      nzFooter: null,
    });
    this.ymService.reachGoal(
      YmGoalAppointment.CatalogItemDoctorAppointmentModalOpen
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
